<template>
  <div>
    <v-card>
      <v-card-title>
        <v-icon color="primary"> mdi-cog </v-icon>
        <span class="pl-2">{{ $t('settings') }}</span>
      </v-card-title>
      <v-card-text>
        <vue-element-loading :active="isLoading" />
        <v-form ref="form" v-if="settings" v-model="valid" lazy-validation class="my-2">
          <div class="my-4">
            <label class="text--secondary font-weight-bold">{{ $t('general_settings') }}</label>
          </div>
          <v-row>
            <v-col cols="12" md="4" class="my-2">
              <v-autocomplete
                v-model="selectedCurrency"
                :items="currencies"
                dense
                outlined
                item-text="name"
                item-value="id"
                :label="$t('currency')"
                :filter="customFilter"
                persistent-hint
                return-object
                required
                :rules="requiredRules"
                :hint="
                  selectedCurrency != null
                    ? selectedCurrency.code + ' (' + selectedCurrency.symbol + ')'
                    : ''
                "
                @change="currencySelected"
              ></v-autocomplete>
            </v-col>
            <!-- distance_to_stop_to_mark_arrived -->
            <v-col cols="12" md="4" class="my-2">
                <v-text-field
                  v-model="settings.distance_to_stop_to_mark_arrived"
                  type="number"
                  outlined
                  dense
                  :label="$t('distance_to_stop')"
                  placeholder="Enter distance to stop to mark arrived by driver"
                  persistent-hint
                  required
                  :rules="numberRules"
                ></v-text-field>
            </v-col>
            <v-col cols="12" md="4" class="my-2">
                <v-text-field
                  v-model="settings.max_search_radius"
                  type="number"
                  outlined
                  dense
                  :label="$t('search_radius')"
                  placeholder="Enter search radius for stops by the guardian"
                  persistent-hint
                  required
                  :rules="numberRules"
                ></v-text-field>
            </v-col>
          </v-row>
          <div class="my-4">
            <label class="text--secondary font-weight-bold">{{ $t('driver_announcement_settings') }}</label>
          </div>
          <v-row>
            <v-col cols="12" md="4" class="my-2">
                <v-text-field
                  v-model="settings.distance_to_drop_off"
                  type="number"
                  outlined
                  dense
                  :label="$t('announcement_distance_dropoff')"
                  placeholder="Enter announcement distance to drop off for the driver"
                  persistent-hint
                  required
                  :rules="numberRules"
                ></v-text-field>
            </v-col>
            <v-col cols="12" md="4" class="my-2">
                <v-text-field
                  v-model="settings.distance_to_pick_up"
                  type="number"
                  outlined
                  dense
                  :label="$t('announcement_distance_pickup')"
                  placeholder="Enter announcement distance to pick up for the driver"
                  persistent-hint
                  required
                  :rules="numberRules"
                ></v-text-field>
            </v-col>
            <v-col cols="12" md="4" class="my-2">
                <v-text-field
                  v-model="settings.distance_to_slow_down"
                  type="number"
                  outlined
                  dense
                  :label="$t('announcement_distance_slowdown')"
                  placeholder="Enter announcement distance to slow down for the driver"
                  persistent-hint
                  required
                  :rules="numberRules"
                ></v-text-field>
            </v-col>
          </v-row>
          <div class="mt-8">
            <label class="text--secondary font-weight-bold">{{ $t('admin_information') }}
                <info-tool-tip class="ml-2"
            message="Admin information will be displayed in the apps."></info-tool-tip>
            </label>
          </div>
          <v-row class="my-2">
            <v-col cols="12" md="4">
                <v-text-field
                    v-model="settings.admin_email"
                    outlined
                    dense
                    :label="$t('admin_email')"
                    placeholder="Enter admin email"
                    persistent-hint
                ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
                <v-text-field
                    v-model="settings.admin_phone"
                    outlined
                    dense
                    :label="$t('admin_phone')"
                    placeholder="Enter admin phone"
                    persistent-hint
                ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
                <v-text-field
                    v-model="settings.admin_address"
                    outlined
                    dense
                    :label="$t('admin_address')"
                    placeholder="Enter admin address"
                    persistent-hint
                ></v-text-field>
            </v-col>
          </v-row>
          <div class="mt-8">
            <label class="text--secondary font-weight-bold">{{ $t('allow_otp_setting') }}             <info-tool-tip class="ml-2"
            message="Allow or disallow OTP for the mobile apps. If enabled, the driver, guardian will receive an OTP to verify their email address before they can sign in."></info-tool-tip></label>
          </div>
          <v-row>
            <v-col cols="12" md="6">
              <v-checkbox
                v-model="settings.otp_required"
                :label="$t('allow_otp')"
                persistent-hint
              ></v-checkbox>
            </v-col>
          </v-row>
          <div class="mt-8">
            <label class="text--secondary font-weight-bold">{{ $t('hide_payment_screen') }}            <info-tool-tip class="ml-2"
            message="Hide or show the payment screen in the guardian app. If enabled, the guardian will not be able to buy coins."></info-tool-tip></label>
          </div>
          <v-row>
            <v-col cols="12" md="6">
              <v-checkbox
                v-model="settings.hide_payment_parents"
                :label="$t('hide_payment_screen_parent')"
                persistent-hint
              ></v-checkbox>
            </v-col>
          </v-row>
          <div class="mt-8">
            <label class="text--secondary font-weight-bold">{{ $t('hide_passengers_setting') }}              <info-tool-tip class="ml-2"
            message="Hide or show all passengers when a driver registers. If enabled, the driver will not be able to search passengers by name, but he will enter the passenger code to register."></info-tool-tip></label>
          </div>
          <v-row>
            <v-col cols="12" md="6">
              <v-checkbox
                v-model="settings.hide_schools"
                :label="$t('hide_passengers')"
                persistent-hint
              ></v-checkbox>
            </v-col>
          </v-row>
          <div class="mt-8">
            <label class="text--secondary font-weight-bold">{{ $t('mode_setting') }}             <info-tool-tip class="ml-2"
            message="Simple mode will hide some routes, trips, and stops from the pessenger admin panel and hide some features from the guardian app."></info-tool-tip></label>
          </div>
          <v-row>
            <v-col cols="12" md="6">
              <v-checkbox
                v-model="settings.simple_mode"
                :label="$t('simple_mode')"
                persistent-hint
              ></v-checkbox>
            </v-col>
          </v-row>
          <div class="mt-8">
            <label class="text--secondary font-weight-bold">{{ $t('ads_settings') }}<info-tool-tip class="ml-2"
            message="Control ads in the driver and guardian app."> </info-tool-tip></label>
          </div>
          <v-row>
            <v-col cols="12" md="6">
              <v-checkbox
                v-model="settings.allow_ads_in_driver_app"
                :label="$t('allow_ads_driver')"
                persistent-hint
              ></v-checkbox>
              <v-checkbox
                v-model="settings.allow_ads_in_parent_app"
                :label="$t('allow_ads_guardian')"
                persistent-hint
              ></v-checkbox>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                  v-model="$i18n.locale"
                  :items="locales"
                  item-text="label"
                  item-value="code"
                  label="Language"
                  outlined
                  dense
                  class="mb-4"
                />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn :disabled="!valid" color="primary" @click="saveSettings">
            {{ $t('save') }}
            <v-icon right dark> mdi-content-save </v-icon>
          </v-btn>
        </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import InfoToolTip from "@/components/InfoToolTip";
import VueElementLoading from "vue-element-loading";
import auth from '@/services/AuthService'
export default {
  components: {
    VueElementLoading,
    InfoToolTip
  },
  data() {
    return {
      currencies: [],
      isLoading: false,
      settings: null,
      valid: true,
      requiredRules: [(v) => !!v || "Required."],
      numberRules: [(v) => /^(0*[1-9][0-9]*(\.[0-9]+)?|0+\.[0-9]*[1-9][0-9]*)$/.test(v) || "Must be greater than 0"],
      percentRules: [(v) => /^100$|^[0-9]{1,2}$|^[0-9]{1,2}\,[0-9]{1,3}$/.test(v) || "Must be from 0 to 100"],
      selectedCurrency: null,
      locales: [
        { code: 'en', label: 'English' },
        { code: 'ar', label: 'العربية' }
      ]
    };
  },
  mounted() {
    this.loadSettings();
  },
  methods: {
    customFilter (item, queryText, itemText) {
      const textOne = item.name.toLowerCase()
      const textTwo = item.code.toLowerCase()
      const searchText = queryText.toLowerCase()

      return textOne.indexOf(searchText) > -1 ||
        textTwo.indexOf(searchText) > -1
    },
    //API Calls
    saveSettings() {
      this.submiting = true;
      axios
        .post("/settings/update", this.settings)
        .then((response) => {
          this.submiting = false;
          this.$notify({
            title: "Success",
            text: "Settings updated!",
            type: "success",
          });
        })
        .catch((error) => {
          this.submiting = false;
          this.$notify({
            title: "Error",
            text: "Error updating settings",
            type: "error",
          });
          console.log(error);
        });
    },
    loadSettings() {
      this.isLoading = true;
      this.settings = [];
      axios
        .get(`/settings/all`)
        .then((response) => {
          this.settings = response.data;
          this.loadCurrencies();
          this.selectedCurrency = this.settings.currency;
        })
        .catch((error) => {
          this.$notify({
            title: "Error",
            text: "Error while retrieving settings",
            type: "error",
          });
          console.log(error);
          auth.checkError(error.response.data.message, this.$router, this.$swal);
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    loadCurrencies() {
      this.isLoading = true;
      this.currencies = [];
      axios
        .get(`/currencies/all`)
        .then((response) => {
          this.currencies = response.data;
        })
        .catch((error) => {
          this.$notify({
            title: "Error",
            text: "Error while retrieving currencies",
            type: "error",
          });
          console.log(error);
          this.$swal("Error", error.response.data.message, "error");
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    validate() {
      this.valid = false;
      let v = this.$refs.form.validate();
      if (v) {
        this.valid = true;
        return true;
      }
      return false;
    },
    currencySelected(r) {
      this.selectedCurrency = r;
      this.settings.currency_id = this.selectedCurrency.id;
    },
  },
};
</script>
